export interface CaseItem {
  slug: string;
  category: '設計・施工実績' | '運用事例・実測' | 'シミュレーション';
  badgeColor: 'cyan' | 'navy' | 'gold';
  title: string;
  subtitle: string;
  facilityType: string;
  scaleInfo: string;
  scope: string[];
  summary: string;
  challenge: string;
  solution: string;
  outcomes: {
    label: string;
    value: string;
    note?: string;
  }[];
  conditionsNote: string;
  details: {
    heading: string;
    body: string;
  }[];
}

export interface NewsItem {
  slug: string;
  date: string;
  category: '企業情報' | '製品・サービス' | '展示会・登壇' | '開発・協業';
  title: string;
  isPastEvent?: boolean;
  summary: string;
  content: string[];
  relatedLink?: {
    label: string;
    to: string;
  };
}

// Verified 2026-10-31: NTA/gBizINFO corporate number 8030001095104.
// Representative, dates, capital and office: https://spaq.co.jp/company/
// Corporate history: https://spaq.co.jp/morikawa_yugo/
// gBizINFO does not provide a representative name or capital for this company.
export const COMPANY_INFO = {
  name: 'Spaq株式会社',
  nameEn: 'Spaq inc.',
  corporateNumber: '8030001095104',
  representative: '代表取締役 森川 祐后',
  foundedNote: '2012年 株式会社トライデント・フレームワークス設立 ／ 2022年 Spaq株式会社へ社名変更',
  founded: '2009年5月1日',
  established: '2012年11月12日',
  capital: '1,000万円',
  address: '〒343-0843 埼玉県越谷市蒲生茜町7-7 TGKビル101',
  tel: '048-961-8427',
  hours: '平日 9:00〜18:00（土日祝休業）',
  businessItems: [
    '電力インテリジェンス事業（需要分析・電力最適設計・エネルギーマネジメント）',
    '自家消費型太陽光発電シミュレーション「1minute solar」の開発・提供',
    '現場設置型エネルギー監視・協調制御システム「SPAQ CORE」の開発・提供',
    '太陽光発電・蓄電システム・高効率空調設備の設計・調達・施工・保守支援',
    'クリエイティブ事業（商業施設・自治体向けイルミネーション企画・設計・施工等）',
  ],
  trackRecordSummary: [
    {
      label: '太陽光発電システムの設置・施工関与実績',
      value: '2,000',
      unit: '件以上',
      note: '※創業以来の住宅・産業用・自家消費型を含む累計の設置・施工関与実績',
    },
    {
      label: '発電所・自家消費設備の設計・検討実績',
      value: '3,000',
      unit: '件以上',
      note: '※導入前のレイアウト設計・収支試算・系統検討を含む累計の設計・検討件数',
    },
    {
      label: '創業・太陽光発電事業への取り組み',
      value: '2009',
      unit: '年〜',
      note: '※2009年創業、2012年法人設立、2022年にSpaq株式会社へ社名変更',
    },
  ],
  history: [
    {
      year: '2009年',
      title: 'トライデント・フレームワークス創業',
      description: '森川祐后がクリスマスイルミネーションのディスプレイデザインやイベント企画の事業を創業。同時期に太陽光発電事業の設計・施工を手掛ける。',
    },
    {
      year: '2012年',
      title: '株式会社トライデント・フレームワークスを設立',
      description: '2012年11月12日に法人設立。太陽光発電事業を本格的に拡大。',
    },
    {
      year: '2018年',
      title: '自家消費事業へ転換',
      description: 'FIT事業から自家消費事業へ完全に事業転換。',
    },
    {
      year: '2022年',
      title: 'Spaq株式会社へ社名変更',
      description: 'Saving power and quotationを由来とするSpaq株式会社へ社名を変更。',
    },
    {
      year: '2024年〜',
      title: '「1minute solar」および「SPAQ CORE」の開発・展開',
      description: 'これまで蓄積した膨大な設計・現場知見をソフトウェアと制御技術へ昇華。導入前の意思決定を支える「1minute solar」と、導入後の太陽光・空調・蓄電池の協調運用を支える「SPAQ CORE」を展開し、「電力インテリジェンス」企業へと進化。',
    },
  ],
};

export const CASES_DATA: CaseItem[] = [
  {
    slug: 'cold-storage-simulation-model',
    category: 'シミュレーション',
    badgeColor: 'gold',
    title: '冷凍冷蔵倉庫（契約電力 450kW）における太陽光・空調・蓄電池の協調運用モデル',
    subtitle: '夏場のデマンドピーク抑制と、庫内温度の安定維持を両立する設計・制御試算',
    facilityType: '冷凍冷蔵倉庫・物流拠点（延床面積 約8,500㎡）',
    scaleInfo: '高圧受電 450kW ／ 自家消費太陽光 200kW ＋ 産業用蓄電池 100kWh ＋ 冷凍機・空調デマコン連携',
    scope: ['1minute solar 初期検討', '30分デマンド波形分析', 'SPAQ CORE 協調制御設計'],
    summary:
      '24時間稼働の冷凍冷蔵倉庫において、昼間の太陽光自家消費でベース購入電力を抑えつつ、30分デマンド時限の終盤に発生しがちな突発ピークを空調（デマコン）と蓄電池の協調で無理なく抑えるモデル試算です。',
    challenge:
      '夏場の外気温上昇と入出庫作業が重なる時間帯に受電電力が跳ね上がり、年間を通じた基本料金（契約電力）を押し上げていました。一方で、従来のデマンドコントローラーによる一律の強制停止では庫内温度の上昇リスクがあり、現場が安心して使える運用が求められていました。',
    solution:
      'まず「1minute solar」と30分電力量データを用いて、過積載しすぎず自家消費率が高まる太陽光容量（200kW）を設計。その上で「SPAQ CORE」により、①太陽光の自家消費で昼間の購入電力をベースダウンし、②空調・冷凍機を無理のない範囲で整え、③どうしても残る時限後半のピークに対して蓄電池をピンポイント放電する協調運用モデルを構築しました。',
    outcomes: [
      {
        label: '想定デマンドピーク削減幅',
        value: '約 65 kW',
        note: '※試算条件に基づく試算例（450kW → 385kW相当）',
      },
      {
        label: '年間購入電力量の削減見込み',
        value: '約 22 %',
        note: '※太陽光自家消費＋ピークシフトによる年間試算値',
      },
      {
        label: '無理な空調全止め回数',
        value: '大幅抑制',
        note: '※蓄電池先行・デマコン協調により過度な室温変化を回避',
      },
    ],
    conditionsNote:
      '※本データは代表的な冷凍冷蔵倉庫の30分デマンド波形をもとにした【試算条件に基づく試算例（シミュレーション）】です。実際の削減効果は施設の稼働状況・気象条件・電力契約メニューにより異なります。',
    details: [
      {
        heading: '1. 電気の使い方（負荷曲線）に合わせた設備容量の絞り込み',
        body: '屋根面積いっぱいにパネルを並べるのではなく、休日のベース負荷や昼間の稼働パターンを分析し、余剰電力を無駄にせず使い切れる容量バランスを算出しました。',
      },
      {
        heading: '2. 空調と蓄電池が「けんか」しない協調制御の設計',
        body: '蓄電池の放電余裕がある間は過度な空調停止を抑止し、本当に必要な場面だけで段階的に空調制御と蓄電池放電を組み合わせることで、現場の作業環境・庫内品質を守りながら契約電力を引き下げる設計としています。',
      },
    ],
  },
  {
    slug: 'manufacturing-plant-solar-design',
    category: '設計・施工実績',
    badgeColor: 'cyan',
    title: '金属加工工場（北関東）における自家消費型太陽光発電の最適設計・導入',
    subtitle: '休日の稼働減による余剰ロスを防ぎ、投資回収性を高めた自家消費設計',
    facilityType: '製造業・金属加工工場（高圧受電）',
    scaleInfo: '屋根設置型 自家消費太陽光発電 180kW（逆潮流防止制御付）',
    scope: ['現地調査・屋根構造確認', '需要データ分析・容量設計', '機器調達・施工・連系手続き'],
    summary:
      '平日の昼間は大きな電力を使うものの、土日祝日は稼働が大きく落ちる工場において、「発電した電気をどれだけ無駄なく自家消費できるか」を重視してパネル容量・PCS容量を最適設計し、施工まで一貫して担当した事例です。',
    challenge:
      '他社からは屋根面積最大の300kW超の提案を受けていましたが、週末や中間期に使い切れない電気が大量に発生する懸念があり、設備投資額に対する実質的な電気代削減メリットが見えにくい状態でした。',
    solution:
      '過去12ヶ月分の30分デマンドデータと稼働カレンダーを照合し、複数パターンの容量で自家消費率と投資回収年数を比較。最も費用対効果の高い180kW構成をご提案し、折板屋根への負荷を抑えた施工と逆潮流防止制御の構築までワンストップで実施しました。',
    outcomes: [
      {
        label: '発電電力の自家消費率',
        value: '94 %以上',
        note: '※導入後の年間稼働ベースでの有効活用率',
      },
      {
        label: '初期設備投資の圧縮',
        value: '約 35 %減',
        note: '※過大な最大設置案と比較した適正投資化',
      },
      {
        label: '施工〜運用開始後の安定稼働',
        value: '順調に推移',
        note: '※現地条件に即したケーブル配線・保護協調を実施',
      },
    ],
    conditionsNote:
      '※本件は当社が設計・調達・施工を担当した【設計・施工実績】に基づく代表例です（守秘義務により施設名・一部数値を丸めています）。',
    details: [
      {
        heading: '「たくさん載せる」ではなく「賢く使い切る」設計',
        body: '太陽光発電は、施設の電気の使い方と噛み合って初めて高い経済性を発揮します。SPAQでは導入前のシミュレーション段階で「載せすぎによる無駄」を可視化し、経営判断しやすい根拠をご提示しました。',
      },
      {
        heading: '将来の蓄電池・制御追加も見据えた盤・配線計画',
        body: 'まずは太陽光発電単体で導入しつつ、将来的に電気料金体系の変化や生産ライン増設があった際に、蓄電池やSPAQ COREをスムーズに後付けできるよう、受変電設備周辺の計測・配線ルートに配慮した施工を行いました。',
      },
    ],
  },
  {
    slug: 'commercial-facility-ems-operation',
    category: '運用事例・実測',
    badgeColor: 'navy',
    title: '複合商業・業務施設におけるデマンド監視と空調・電源の協調運用検証',
    subtitle: '来館者の快適性を損なわずに、30分時限の電力ピークをなだらかに整える現場運用',
    facilityType: '商業・業務複合施設',
    scaleInfo: '高圧受電施設 ／ 既存デマンドコントローラー連携 ＋ リアルタイム電力監視',
    scope: ['電力計測・可視化', 'デマンド時限協調ロジック検証', '運用チューニング'],
    summary:
      '来館者やテナントの快適性が最優先される商業・業務施設において、従来の「警報が鳴ったら一斉に空調を止める」運用から脱却し、30分時限の推移を見守りながら段階的・協調的にピークを抑える運用を検証・実践しています。',
    challenge:
      '猛暑日の午後にデマンド警報が頻発し、空調が急に弱まることで「店内が暑い」という声が出る一方、警報を緩めると契約電力が跳ね上がってしまうジレンマを抱えていました。',
    solution:
      '受電点および主要系統の電力をリアルタイムに捉え、30分時限の開始から終了までの積算ペースを常時予測。余裕がある時間帯は無理な空調抑制を控え、本当にピーク超過が迫った局面のみ段階的に制御を強める協調運用を導入しました。',
    outcomes: [
      {
        label: '不必要な空調強制御の回避',
        value: '大幅低減',
        note: '※時限序盤〜中盤の過剰反応による室温悪化を防止',
      },
      {
        label: '目標デマンド値の維持',
        value: '超過ゼロ',
        note: '※検証期間中の目標デマンド枠内での安定運用',
      },
      {
        label: '施設管理者の負担軽減',
        value: '自動協調化',
        note: '※警報時の手動対応・現場駆けつけを削減',
      },
    ],
    conditionsNote:
      '※本件は現場計測・協調制御ロジックの【運用事例・実測検証】に基づく内容です。施設環境や既存機器の仕様により連携範囲は異なります。',
    details: [
      {
        heading: '30分時限の「いま何分目か」を踏まえた賢い判断',
        body: '電力会社の契約電力が決まる30分時限では、開始3分目の瞬間的な電力上昇と、残り3分での電力上昇では意味がまったく異なります。時間の経過と積算電力量を正確に追うことで、落ち着いて効果を確認しながら制御を行います。',
      },
    ],
  },
];

export const NEWS_DATA: NewsItem[] = [
  {
    slug: 'corporate-site-renewal-2026',
    date: '2026.10.23',
    category: '企業情報',
    title: 'コーポレートサイトを全面リニューアルしました —「電力インテリジェンス」で設計から制御までを一貫支援',
    summary:
      '導入判断を支える「1minute solar」と、現場の電力運用を支える「SPAQ CORE」、そして創業以来の設計・施工事業を統合し、より分かりやすくお伝えするコーポレートサイトへ刷新いたしました。',
    content: [
      'Spaq株式会社は、本日コーポレートサイトを全面リニューアルいたしました。',
      '当社は2009年の創業時期から太陽光発電の設計・施工を手掛け、2012年に法人設立、2022年にSpaq株式会社へ社名を変更しました。これまで数多くの発電所および自家消費型太陽光設備の設計・施工に携わってまいりました。',
      '現在、電気料金の高騰や脱炭素への要請を背景に、「太陽光や蓄電池を入れるべきか分からない」「導入した設備を現場でうまく使いこなせていない」というお悩みが増えています。',
      '新しいコーポレートサイトでは、当社の核となる考え方を「電力インテリジェンス（電気を、もっと賢く、無駄なく。）」として整理し、導入前の判断を支える『1minute solar』と、導入後の協調運用を支える『SPAQ CORE』の両輪で、お客様の電力課題に向き合う姿勢を分かりやすくご紹介しています。',
    ],
    relatedLink: {
      label: '技術と開発（電力インテリジェンス）を見る',
      to: '/technology',
    },
  },
  {
    slug: 'spaq-core-demand-coordination-update',
    date: '2026.09.15',
    category: '製品・サービス',
    title: '現場型エネルギー監視・制御「SPAQ CORE」のデマンドコントローラー協調シーケンスをアップデート',
    summary:
      '30分のデマンド時限において、太陽光の自家消費・空調制御・蓄電池充放電をより滑らかに連携させ、室温の快適性とピーク抑制を両立する新しい5段階協調ロジックを実装しました。',
    content: [
      'Spaq株式会社は、施設現場で太陽光・空調・蓄電池を協調運用する「SPAQ CORE」において、デマンドコントローラー（空調制御）との連携ロジックを大幅にアップデートいたしました。',
      '新しい制御シーケンスでは、30分のデマンド時限を通して「まず太陽光を使い切る」「蓄電池の放電余力を活かして不要な空調停止を抑える」「蓄電池だけでは足りない局面で段階的に空調制御を許可する」という優先順位をきめ細かく管理します。',
      'これにより、工場や倉庫、商業施設において、現場の作業環境や快適性を守りながら、無理のないピークカットと基本料金の抑制を実現します。',
    ],
    relatedLink: {
      label: 'SPAQ COREの詳細を見る',
      to: '/products/spaq-core',
    },
  },
  {
    slug: '1minute-solar-partner-expansion',
    date: '2026.07.20',
    category: '開発・協業',
    title: '自家消費型太陽光の概算シミュレーター「1minute solar」を活用した建設・設備パートナー様との連携を強化',
    summary:
      '初回商談や現地調査前の段階で、屋根条件や電気の使い方に応じた太陽光の導入効果を素早く試算できる「1minute solar」のパートナー連携相談窓口を拡充しました。',
    content: [
      '自家消費型太陽光発電の提案現場では、「詳細な図面や30分デマンドデータが揃うまで、お客様に概算すら出せない」「過大な容量を提案してしまい、後から投資回収が合わなくなる」といった課題が多く見られます。',
      '当社の『1minute solar』は、限られた初期情報からでも施設の電力需要と太陽光発電の相性を素早く把握し、複数パターンの比較検討を可能にするソフトウェアです。',
      '建設会社様、不動産管理会社様、設備工事会社様、販売パートナー様向けに、初期提案から詳細設計・施工・制御導入までを分担して進められる協業体制をご案内しております。',
    ],
    relatedLink: {
      label: '協業・パートナーページを見る',
      to: '/partners',
    },
  },
  {
    slug: 'energy-exhibition-report-2026',
    date: '2026.05.18',
    category: '展示会・登壇',
    isPastEvent: true,
    title: '【開催終了】エネルギーマネジメント・脱炭素ソリューション関連展示会への出展および技術紹介のご報告',
    summary:
      '多くの企業・自治体・設備事業者の皆様にブースへお立ち寄りいただき、「1minute solar」と「SPAQ CORE」による設計〜制御の一貫アプローチをご紹介いたしました。（※本展示会は終了しております）',
    content: [
      '※本イベントはすでに終了しております。ご来場いただいた皆様に心より御礼申し上げます。',
      '会期中は、工場・物流倉庫・商業施設の施設担当者様や、提案力強化を目指す建設・設備パートナー様より、「太陽光単体ではなく、空調や蓄電池まで含めてどう設計・制御すべきか」という具体的なご相談を多数いただきました。',
      '展示会場でご紹介した資料やデモ画面のご説明は、個別オンライン相談にて随時承っております。お気軽にお問い合わせください。',
    ],
    relatedLink: {
      label: '個別説明・ご相談はこちら',
      to: '/contact',
    },
  },
];

export const ONE_MINUTE_SOLAR_FAQS = [
  {
    q: '「1minute solar」を使うのに、専門的な図面や詳細な電力データは必要ですか？',
    a: 'いいえ、初期の概算検討では、施設の所在地（エリア）、おおよその屋根面積や設置イメージ、月々の電気代や業種・稼働時間といった基本的な情報から試算を始めることができます。より精緻な検討に進む段階で、30分デマンドデータや図面をお預かりして正式設計へ引き継ぎます。',
  },
  {
    q: '一般的な太陽光シミュレーターと何が違うのですか？',
    a: '単に「屋根に何kW載って、年間いくら発電するか」だけを計算するのではなく、「その施設の電気の使い方（昼間の需要や休日の稼働）に対して、どれだけ無駄なく自家消費できるか」という【需要との相性】を重視している点が大きな特長です。過大な設備投資を防ぎ、納得感のある容量選びを支えます。',
  },
  {
    q: 'シミュレーション結果を見た後、必ず工事を発注しなければいけませんか？',
    a: 'いいえ、まずは「自社の施設に太陽光が向いているのか」「どのくらいの規模感が適正か」を判断するためのツールとしてご活用いただけます。試算結果をもとに「見送る」「時期を改める」「蓄電池や空調制御（SPAQ CORE）と組み合わせて再検討する」といった冷静な経営判断にお役立てください。',
  },
  {
    q: '販売店や建設会社が、自社のお客様への提案に活用することはできますか？',
    a: 'はい、可能です。設備会社様・建設会社様・販売パートナー様が、お客様との初回商談で素早く方向性を共有するためのツールとしてご活用いただき、その後の詳細設計や施工、SPAQ COREの組み込みを当社がバックアップする協業モデルをご用意しています。',
  },
];

export const SPAQ_CORE_FAQS = [
  {
    q: '既存のデマンドコントローラー（空調制御）や太陽光設備があっても導入できますか？',
    a: '施設の既存機器のメーカー・型番・通信仕様によって連携方法が異なります。現地調査および事前ヒアリングにて、既存のデマンドコントローラーやパワーコンディショナ、蓄電池との接続条件を確認の上、最適な構成をご提案いたします。',
  },
  {
    q: '蓄電池をまだ導入していない施設でも相談できますか？',
    a: 'はい、もちろんです。まずは太陽光発電と空調制御（デマンド管理）の組み合わせから検討し、本当に蓄電池が必要なピーク波形かどうかを診断することも可能です。蓄電池の新設を含めたトータル設計にも対応しています。',
  },
  {
    q: 'なぜ「空調を止める」よりも先に「太陽光の自家消費」や「蓄電池の活用」を行うのですか？',
    a: '工場や倉庫、店舗において、過度な空調停止は室温の悪化を招き、従業員の作業効率低下や商品の品質リスクにつながるためです。SPAQ COREは、まず太陽光の電気を賢く使い、無理のない範囲で需要を整え、それでも残る鋭いピークに対して蓄電池と空調制御を協調させることで、「現場の快適性」と「電気代削減」の両立を目指します。',
  },
  {
    q: 'インターネット回線が一時的に切れた場合、現場の制御はどうなりますか？',
    a: 'SPAQ COREは施設現場に設置されたエッジ端末がリアルタイムに計測・判断を行うため、万が一クラウドとの通信が一時的に途絶えた場合でも、現場内の安全ルールに従って自立的に監視・保護動作を継続する設計となっています。',
  },
];
